export const CLIENT_EXPERIENCE_MANAGER_REVIEW_DATA = [
    {
        id: 1,
        title: "Client Experience Ownership & Communication",
        definition: "Owns the client relationship, communication cadence, and WOW experience.",
        questions: [
            "Delivers consistent weekly client updates for active clients by the agreed deadline (Performance Points).",
            "Delivers consistent weekly client updates for FSP clients by the agreed deadline (Performance Points).",
            "Responds to active client messages within 24 hours and does not let communication sit (Performance Points).",
            "Sets clear expectations with clients and prevents surprises through proactive communication.",
            "Communicates difficult information early, clearly, and professionally (no avoidance).",
            "Maintains a calm, confident tone that reduces client anxiety and builds trust.",
            "Protects the client relationship during stressful moments and prevents escalation.",
            "Runs organized, outcome-focused client conversations that end with clarity and next steps.",
            "Drives client satisfaction (WOW score) at or above standard (Scorecard).",
            "Creates a consistent “LIDA experience” that clients would enthusiastically recommend."
        ]
    },
    {
        id: 2,
        title: "Selections, Decisions & Milestone Control",
        definition: "Drives timely decisions and keeps selections aligned to schedule and standards.",
        questions: [
            "Maintains selections/to-do lists so they are current and accurate (Performance Points).",
            "Prevents selections from going past milestones by driving decisions early and tracking deadlines (Scorecard / Milestones).",
            "Educates clients on what decisions are needed, by when, and why timing matters.",
            "Creates clarity on scope, allowances, and expectations before selections are finalized.",
            "Coordinates selections with the build schedule so the site is never waiting on decisions.",
            "Prevents last-minute changes by managing client indecision and decision fatigue.",
            "Ensures selections are documented in the correct location and in the correct format every time.",
            "Confirms stakeholders (client/PM) understand what was decided and what happens next.",
            "Owns the selections timeline as a leadership responsibility—not an admin task.",
            "Protects the project from rework by making decisions clear, complete, and buildable."
        ]
    },
    {
        id: 3,
        title: "Documentation, Drawings & Specs Accuracy",
        definition: "Produces accurate, organized, buildable documentation with strong version control.",
        questions: [
            "Maintains clean, current specs and drawings—removing old versions and preventing duplicate/conflicting information (Performance Points).",
            "Ensures design plans and measurements are correct before release (Performance Points).",
            "Produces documentation that is clear enough that the site and trades can execute without confusion.",
            "Maintains strong version control so the team always knows what is current.",
            "Creates selection/spec packages that are complete, organized, and easy to use.",
            "Prevents documentation-driven RFIs by catching gaps before they hit site.",
            "Protects the client by documenting decisions in a way that prevents disputes later.",
            "Protects the builder by ensuring documentation is auditable and defensible.",
            "Maintains a high standard of detail and accuracy under pressure.",
            "Treats documentation quality as a core craft standard—not a paperwork requirement."
        ]
    },
    {
        id: 4,
        title: "Process Discipline & Admin Execution (Buildertrend / Scorecard)",
        definition: "Executes weekly operating discipline through systems, not memory.",
        questions: [
            "Completes the Ninety scorecard on time every week (Performance Points).",
            "Maintains accurate time clock entries with notes by the standard deadline (Performance Points).",
            "Reviews active bids status and responds to RFIs consistently (Performance Points).",
            "Checks that POs are saved and released correctly, preventing procurement issues (Performance Points).",
            "Maintains Buildertrend hygiene: tasks, selections, docs, and notes are current and in the right place (supports Scorecard).",
            "Uses checklists and repeatable processes rather than relying on memory.",
            "Prevents avoidable handoff failures by confirming the PM/site has what they need before it becomes urgent.",
            "Keeps internal commitments and deadlines without needing reminders or escalation.",
            "Improves workflows and suggests system improvements instead of working around the process.",
            "Demonstrates reliable weekly execution as reflected in Performance Points % (Scorecard)."
        ]
    },
    {
        id: 5,
        title: "Error Prevention & Financial Responsibility",
        definition: "Prevents costly mistakes and protects LIDA’s time, money, and reputation.",
        questions: [
            "Operates with a prevention mindset: catches issues early before they become expensive or emotional.",
            "Does not create avoidable costs to the company (e.g., >$100 time/material mistakes) (Performance Points).",
            "Does not create major avoidable costs to the company (e.g., >$500 time/material mistakes) (Performance Points).",
            "Prevents rework by ensuring decisions and documentation are correct before site execution.",
            "Understands that late decisions and poor documentation have real financial impact on schedule and margin.",
            "Owns mistakes quickly, corrects them, and implements a prevention step so they do not repeat.",
            "Escalates risks early when the cost of delay or error is rising.",
            "Balances client desires with budget realities and guides value decisions professionally.",
            "Maintains professional diligence when workload is high (no quality drop).",
            "Protects LIDA’s reputation and profitability through disciplined execution."
        ]
    },
    {
        id: 6,
        title: "Cross-Team Coordination (PM / Site / Trades)",
        definition: "Integrates design, client decisions, and production realities to prevent friction.",
        questions: [
            "Maintains strong working relationships with PMs built on clarity, reliability, and trust.",
            "Proactively communicates what the PM/site needs next—before they have to ask.",
            "Coordinates with site and trades so selections and documentation match real field conditions.",
            "Creates clean internal handoffs that reduce friction and rework.",
            "Resolves cross-team issues quickly and professionally without blame or drama.",
            "Ensures the PM/site is never surprised by late decisions or missing information.",
            "Communicates constraints and tradeoffs clearly (time, cost, feasibility).",
            "Acts as an integrator: aligns client, design, and production realities.",
            "Leads meetings and conversations that produce decisions and alignment.",
            "Builds credibility so the field team trusts the information and follows it."
        ]
    },
    {
        id: 7,
        title: "Planning, Workload & Throughput Management",
        definition: "Manages workload and priorities so commitments are met consistently.",
        questions: [
            "Maintains weekly workload discipline and reports workload accurately (Scorecard: Weekly Workload Rating).",
            "Plans ahead so client, selections, and documentation deadlines are met without last-minute rush.",
            "Prioritizes high-impact work that protects the client experience and the build schedule.",
            "Manages multiple projects without dropping critical balls.",
            "Uses a clear personal system to track commitments, decisions, and follow-ups.",
            "Escalates capacity constraints early rather than silently falling behind.",
            "Maintains quality under pressure—does not trade accuracy for speed.",
            "Creates predictable throughput so PMs can plan confidently.",
            "Builds a repeatable cadence for weekly execution (updates, selections review, RFIs/POs).",
            "Demonstrates consistent weekly performance rather than spikes and crashes (Performance Points %)."
        ]
    },
    {
        id: 8,
        title: "Culture, Professionalism & Leadership Presence",
        definition: "Lives LIDA values, is coachable, and elevates the standard.",
        questions: [
            "Demonstrates LIDA core values in day-to-day behavior (Performance Points includes Core Values shout-out).",
            "Goes above and beyond in a way that creates a WOW experience for clients and team (Performance Points).",
            "Maintains a professional, calm, and respectful presence in all interactions.",
            "Is coachable and open to feedback without defensiveness.",
            "Takes ownership when things go wrong and leads solutions.",
            "Contributes positively to culture and team morale.",
            "Supports others (PMs, designers, admin) through collaboration and clarity.",
            "Seeks improvement and training to raise skill level in client leadership and design execution.",
            "Shares lessons learned and helps improve systems and standards.",
            "Raises the standard of the company, not just their own workload."
        ]
    }
];
